/**
 * Oath Power Report Service
 * API calls for power reporting operations using the new Oath authentication
 * Routes to /oath/powerreports/* endpoints in OathPowerReportsController
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Coerce a numeric field to non-negative — used to hide credits at the service boundary
 * so callers never see negative net/buf/misc on a batch. Empty/null/NaN all become 0.
 */
const clampToZero = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num) || num <= 0) return 0;
  return num;
};

/**
 * Hide credits from a batch payload. Every payment bucket (totalNet/Buf/Misc and the legacy
 * openNet/Buf/Misc aliases) is clamped to >= 0 so credits render as $0 wherever they're
 * shown. The batch object is shallow-cloned so the caller's reference is unchanged.
 */
const hideCreditsOnBatch = (batch) => {
  if (!batch) return batch;
  const net  = clampToZero(batch.totalNet  ?? batch.openNet);
  const buf  = clampToZero(batch.totalBuf  ?? batch.openBuf);
  const misc = clampToZero(batch.totalMisc ?? batch.openMisc);
  return {
    ...batch,
    totalNet: net,  openNet: net,
    totalBuf: buf,  openBuf: buf,
    totalMisc: misc, openMisc: misc,
  };
};

/**
 * Sanitize a batches array: clamp every bucket to >= 0 and drop batches with nothing
 * payable left. A mixed-sign batch (e.g. +BUF, -NET) keeps the positive bucket and
 * displays $0 for the credit bucket; a fully-credit batch is dropped entirely so the
 * user never sees a row of zeros that hints credits exist.
 */
const sanitizeBatchesHideCredits = (batches) =>
  (batches || [])
    .map(hideCreditsOnBatch)
    .filter(b => b && (b.totalNet + b.totalBuf + b.totalMisc) > 0);

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - handles BaseResponse wrapper
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// REPORT MANAGEMENT
// ============================================

/**
 * Get active (draft) power reports for an agency
 * @param {number} agencyId - Agency ID
 * @param {number} pageNumber - Page number (1-indexed)
 * @param {number} pageSize - Items per page
 * @param {string} searchTerm - Optional search term
 */
export const getActiveReports = async (agencyId, pageNumber = 1, pageSize = 10, searchTerm = '', includeSubAgencies = true) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      includeSubAgencies: includeSubAgencies.toString(),
    });
    if (searchTerm) params.append('searchTerm', searchTerm);

    const response = await authFetch(`${BASE_URL}oath/powerreports/active?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get active reports', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get active reports error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get recent (submitted) power reports for an agency.
 * Defaults to the last 6 months server-side; an explicit fromDate/toDate allows unbounded lookback.
 * @param {number} agencyId - Agency ID
 * @param {number} pageNumber - Page number (1-indexed)
 * @param {number} pageSize - Items per page
 * @param {boolean} includeSubAgencies - Include sub-agency reports
 * @param {string} searchTerm - Optional search term
 * @param {string} fromDate - Optional range start ('YYYY-MM-DD')
 * @param {string} toDate - Optional range end, inclusive ('YYYY-MM-DD')
 */
export const getRecentReports = async (agencyId, pageNumber = 1, pageSize = 10, includeSubAgencies = false, searchTerm = '', fromDate = '', toDate = '') => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      includeSubAgencies: includeSubAgencies.toString(),
    });
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);

    const response = await authFetch(`${BASE_URL}oath/powerreports/recent?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get recent reports', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get recent reports error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get batches to pay for an agency
 * @param {number} agencyId - Agency ID
 */
export const getBatchesToPay = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/batches-to-pay?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get batches', data: [] };
    }

    return { success: true, data: sanitizeBatchesHideCredits(result) };
  } catch (error) {
    console.error('Get batches to pay error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get batches for report submission modal - includes regular batches, shipping fees, AND transfer bond batches.
 * Use this when showing the batch selection modal during power report submission.
 * @param {number} agencyId - Agency ID
 */
export const getBatchesForSubmission = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/batches-for-submission?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get batches for submission', data: [] };
    }

    return { success: true, data: sanitizeBatchesHideCredits(result) };
  } catch (error) {
    console.error('Get batches for submission error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get power report details
 * @param {number} reportId - Report ID
 */
export const getReportDetails = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get report details' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get report details error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Create a new power report
 * @param {number} agencyId - Agency ID
 */
export const createReport = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports`, {
      method: 'POST',
      body: JSON.stringify({ agencyId }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to create report' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Create report error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Delete a power report
 * @param {number} reportId - Report ID
 */
export const deleteReport = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}`, {
      method: 'DELETE',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to delete report' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete report error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// POWER MANAGEMENT
// ============================================

/**
 * Get available powers for an agency
 * @param {number} agencyId - Agency ID
 */
export const getAvailablePowers = async (agencyId) => {
  try {
    const url = `${BASE_URL}oath/powerreports/available-powers?agencyId=${agencyId}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get available powers', data: [] };
    }

    // Result is a plain array; keep items fallback for safe deployment ordering
    const items = result?.items || result || [];
    return { success: true, data: Array.isArray(items) ? items : [] };
  } catch (error) {
    console.error('Get available powers error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Add a power to a report
 * Note: Uses legacy endpoint as oath endpoint is not yet implemented
 * @param {Object} powerData - Power data to add
 */
export const addPowerToReport = async (powerData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/powers`, {
      method: 'POST',
      body: JSON.stringify(powerData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to add power' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Add power to report error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Update a power in a report
 * @param {number} powerId - Power ID
 * @param {Object} powerData - Updated power data
 */
export const updatePower = async (powerId, powerData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/powers/${powerId}`, {
      method: 'PUT',
      body: JSON.stringify(powerData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to update power' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Update power error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Delete a power from a report
 * @param {number} reportId - Report ID
 * @param {number} powerId - Power ID
 */
export const deletePowerFromReport = async (reportId, powerId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/powers/${powerId}`, {
      method: 'DELETE',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to delete power' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete power from report error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Check if a power number is already in an active (draft) report
 * @param {string} powerNumber - Power number to check
 * @param {number} [excludeReportId] - Report ID to exclude (current report)
 * @returns {{ success: boolean, data?: { isInActiveReport: boolean, reportNumber?: string, powerReportId?: number } }}
 */
export const checkPowerActiveReport = async (powerNumber, excludeReportId) => {
  try {
    let url = `${BASE_URL}oath/powerreports/powers/${powerNumber}/active-report-check`;
    if (excludeReportId) {
      url += `?excludeReportId=${excludeReportId}`;
    }
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to check active report' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Check power active report error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// AGENCY DATA
// ============================================

/**
 * Get executing agents for an agency
 * @param {number} agencyId - Agency ID
 */
export const getExecutingAgents = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/executing-agents?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agents', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get executing agents error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get cities for an agency
 * @param {number} agencyId - Agency ID
 */
export const getCities = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/cities?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get cities', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get cities error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get combined agency data (agents and cities)
 * @param {number} agencyId - Agency ID
 */
export const getAgencyData = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/agency-data?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agency data' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get agency data error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get sub-agencies for a general agent
 * @param {number} agencyId - Agency ID
 */
export const getSubAgencies = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/sub-agencies?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get sub-agencies', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get sub-agencies error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get agencies that have unpaid batches (for Batches to Pay dropdown filter)
 * @param {number} agencyId - Main agency ID
 * @returns {Object} { success, data: AgencyWithBatchesDto[] }
 */
export const getAgenciesWithBatches = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/agencies-with-batches?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agencies with batches', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get agencies with batches error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get batches to pay AND agencies with batches in a single call.
 * More efficient than calling getBatchesToPay and getAgenciesWithBatches separately.
 * Runs stored procedure calls in parallel on the backend for maximum performance.
 * @param {number} agencyId - Agency ID
 * @returns {Object} { success, data: { batches: PowerReportDto[], agencies: AgencyWithBatchesDto[] } }
 */
export const getBatchesWithAgencies = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/batches-with-agencies?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get batches with agencies', data: null };
    }

    // Hide credits before the data leaves this service. The agency dropdown is fed
    // unpaidBatchCount from the backend, but that count assumes credits count too —
    // recompute it from the sanitized batches so the dropdown numbers match what the
    // user actually sees in the table.
    const batches = sanitizeBatchesHideCredits(result?.batches);
    const countsByAgency = batches.reduce((acc, b) => {
      acc[b.agencyId] = (acc[b.agencyId] || 0) + 1;
      return acc;
    }, {});
    const agencies = (result?.agencies || [])
      .map(a => ({ ...a, unpaidBatchCount: countsByAgency[a.agencyId] || 0 }))
      .filter(a => a.unpaidBatchCount > 0);

    return { success: true, data: { ...result, batches, agencies } };
  } catch (error) {
    console.error('Get batches with agencies error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Hide an agency from user's selection dropdown
 * @param {number} agencyId - Agency ID to hide
 */
export const hideAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/agencies/${agencyId}/hide`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to hide agency' };
    }

    return { success: true };
  } catch (error) {
    console.error('Hide agency error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Unhide an agency, making it visible in the selection dropdown
 * @param {number} agencyId - Agency ID to unhide
 */
export const unhideAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/agencies/${agencyId}/unhide`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to unhide agency' };
    }

    return { success: true };
  } catch (error) {
    console.error('Unhide agency error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// PREMIUM CALCULATION
// ============================================

/**
 * Calculate premium, commission, and BUF for a power
 * @param {Object} request - Calculation request
 * @param {string} request.powerNumber - Power number
 * @param {string} request.executingState - State code
 * @param {Date|string} request.executionDate - Execution date
 * @param {number} request.powerAmount - Bond amount
 * @param {number} request.agencyId - Agency ID
 * @param {number} [request.rateId] - Optional specific rate ID to use
 */
export const calculatePremium = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/calculate-premium`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to calculate premium' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Calculate premium error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get available rates for a power based on state, power type, amount, and execution date
 * Returns both standard (Priority=0) and variable (Priority=1) rates with proof attribute requirements
 * @param {string} powerNumber - Power number
 * @param {string} state - State code
 * @param {Date|string} executionDate - Execution date
 * @param {number} powerAmount - Bond amount
 * @returns {Object} { success, data: { rates: AvailableRateDto[], defaultRateId: number } }
 */
export const getAvailableRates = async (powerNumber, state, executionDate, powerAmount) => {
  try {
    const params = new URLSearchParams({
      powerNumber,
      state,
      executionDate: executionDate instanceof Date ? executionDate.toISOString() : executionDate,
      powerAmount: powerAmount.toString(),
    });

    const response = await authFetch(`${BASE_URL}oath/powerreports/rates?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get available rates', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get available rates error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// RATE CHANGE DETECTION & RECALCULATION
// ============================================

/**
 * Check if any powers in a draft report have outdated commission rates.
 * Called on draft report detail page load.
 * @param {number} reportId - Report ID
 * @returns {Object} { success, data: { hasChanges, affectedPowerCount, oldTotalNet, newTotalNet, oldTotalBuf, newTotalBuf, affectedPowers } }
 */
export const checkRateChanges = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rate-changes`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to check rate changes', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Check rate changes error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Validate that all powers in a draft report still have valid statuses before submission.
 * Called when user clicks "Submit Report" to catch status changes early.
 * @param {number} reportId - Report ID
 * @returns {Object} { success, data: { isValid, totalPowerCount, invalidPowerCount, invalidPowers } }
 */
export const validatePowerStatuses = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/validate-statuses`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to validate power statuses', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Validate power statuses error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Recalculate all powers in a draft report using current commission rates.
 * Called when user clicks "Continue" on the rate change blocking dialog.
 * @param {number} reportId - Report ID
 * @returns {Object} { success, data: PowerReportDetailDto }
 */
export const recalculateReport = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/recalculate`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to recalculate report', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Recalculate report error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Recalculate all active (draft) reports for an agency.
 * "Refresh Reports from Server" button on Active Reports tab.
 * @param {number} agencyId - Agency ID
 * @returns {Object} { success, data: { reportsUpdated, powersUpdated, message } }
 */
export const recalculateAllReports = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/recalculate-all?agencyId=${agencyId}`, {
      method: 'POST',
    });
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to recalculate reports', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Recalculate all reports error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// SUBMISSION & PAYMENT
// ============================================

/**
 * @deprecated Use `getPaymentAccounts` from `oathPaymentAccountService.js` instead.
 * That endpoint supports user-level visibility filtering (shared vs personal accounts).
 * Get bank accounts for an agency (for ACH payment)
 * @param {number} agencyId - Agency ID
 */
export const getBankAccounts = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/bank-accounts?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get bank accounts', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get bank accounts error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Submit a power report with payment
 * @param {number} reportId - Report ID
 * @param {Object} submitData - Submission data including payment info
 */
export const submitReport = async (reportId, submitData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/submit`, {
      method: 'POST',
      body: JSON.stringify(submitData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to submit report' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Submit report error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Submit a power report with optional batch payments in a single combined ACH transaction
 * @param {number} reportId - Report ID
 * @param {Object} submitData - Combined submission data including payment and batch info
 */
export const submitCombinedPayment = async (reportId, submitData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/submit-combined`, {
      method: 'POST',
      body: JSON.stringify(submitData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to submit combined payment' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Submit combined payment error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Pay outstanding batches via ACH
 * @param {Object} paymentData - Payment data with batch IDs and amounts
 */
export const payBatches = async (paymentData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/pay-batches`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to pay batches' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Pay batches error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Void an ACH payment on a report
 * @param {number} reportId - Report ID
 * @param {string} [reason] - Optional reason for voiding
 */
export const voidAchPayment = async (reportId, reason) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/void-ach`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to void ACH payment' };
    }

    return { success: true };
  } catch (error) {
    console.error('Void ACH payment error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// PDF & PROOF
// ============================================

/**
 * Get PDF for a submitted report
 * @param {number} reportId - Report ID
 */
export const getReportPdf = async (reportId) => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
    const response = await fetch(`${BASE_URL}oath/powerreports/${reportId}/pdf`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return { success: false, error: 'Failed to get PDF' };
    }

    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    console.error('Get report PDF error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Upload proof image for a power
 * @param {number} powerId - Power ID
 * @param {File} file - Image file to upload
 */
export const uploadProofImage = async (powerId, file) => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${BASE_URL}oath/powerreports/powers/${powerId}/proof`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to upload proof' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Upload proof image error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get proof image for a power
 * Returns: { success, data: { success, powerReportPowerId, powerNumber, proofBase64, contentType, fileName, hasProof, message } }
 * @param {number} powerId - Power ID
 */
export const getProofImage = async (powerId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/powers/${powerId}/proof`);

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, data: { hasProof: false } }; // No proof exists
      }
      return { success: false, error: 'Failed to get proof' };
    }

    const result = await parseResponse(response);
    return { success: true, data: result };
  } catch (error) {
    console.error('Get proof image error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Delete proof image for a power
 * @param {number} powerId - Power ID
 */
export const deleteProofImage = async (powerId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/powers/${powerId}/proof`, {
      method: 'DELETE',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to delete proof' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete proof image error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// REPORT NUMBER
// ============================================

/**
 * Update report number for a draft report
 * @param {number} reportId - Report ID
 * @param {string} reportNumberSuffix - New report number suffix (the part after the colon)
 */
export const updateReportNumber = async (reportId, reportNumberSuffix) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/report-number`, {
      method: 'PATCH',
      body: JSON.stringify({ reportNumberSuffix }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to update report number' };
    }

    return { success: true, data: result.reportNumber };
  } catch (error) {
    console.error('Update report number error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// REWRITE OPERATIONS
// ============================================

/**
 * Look up an executed power by power number for rewrite operations
 * Returns power details if found and has valid status (E, T, F, J, D)
 * @param {number} agencyId - Agency ID
 * @param {string} powerNumber - Power number to look up
 * @param {number} reportId - Current report ID (to exclude powers already in this report)
 * @returns {Object} { success, data: { power, isInCurrentReport, hasTransferBond, status } }
 */
export const lookupExecutedPowerForRewrite = async (agencyId, powerNumber, reportId) => {
  try {
    const params = new URLSearchParams({
      agencyId: agencyId.toString(),
      powerNumber: powerNumber,
      reportId: reportId.toString()
    });

    const response = await authFetch(`${BASE_URL}oath/powerreports/rewrite/lookup-power?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Power not found or not eligible for rewrite',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Lookup executed power error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get available powers for rewrite (excludes original power, supports search).
 * Returns reportable powers in U, UF, UJ or UD status (same set as Add Power); each item
 * carries `powerStatusCd` so the UI can flag the non-U ones.
 * @param {number} reportId - Report ID
 * @param {string} excludePowerNumber - Power number to exclude (original)
 * @param {number} pageNumber - Page number (1-indexed)
 * @param {number} pageSize - Items per page
 * @param {string} searchTerm - Optional search term
 */
export const getAvailablePowersForRewrite = async (reportId, excludePowerNumber = '', pageNumber = 1, pageSize = 50, searchTerm = '') => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    if (excludePowerNumber) params.append('excludePowerNumber', excludePowerNumber);
    if (searchTerm) params.append('searchTerm', searchTerm);

    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rewrite/available-powers?${params}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get available powers for rewrite', data: [] };
    }

    const items = result?.items || result || [];
    return { success: true, data: Array.isArray(items) ? items : [], totalCount: result?.totalCount || items.length };
  } catch (error) {
    console.error('Get available powers for rewrite error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Validate a rewrite operation before execution
 * @param {number} reportId - Report ID
 * @param {Object} validationData - Validation request data
 * @param {string[]} validationData.originalPowerNumbers - Original power numbers
 * @param {Object[]} validationData.replacementPowers - Replacement power info
 */
export const validateRewrite = async (reportId, validationData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rewrite/validate`, {
      method: 'POST',
      body: JSON.stringify(validationData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Validation failed', data: result };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Validate rewrite error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Execute a rewrite on a draft (unsubmitted) report
 * @param {number} reportId - Report ID
 * @param {Object} rewriteData - Rewrite request data
 * @param {number} rewriteData.powerReportPowerId - Power ID in report to rewrite
 * @param {string} rewriteData.newPowerNumber - New power number
 * @param {number} rewriteData.newBondAmount - Optional new bond amount
 * @param {Date} rewriteData.newExecutionDate - Optional new execution date
 */
export const executeDraftRewrite = async (reportId, rewriteData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rewrite/draft`, {
      method: 'POST',
      body: JSON.stringify(rewriteData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || result.errors?.join(', ') || 'Rewrite failed', data: result };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Execute draft rewrite error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Execute a rewrite on a submitted report
 * @param {number} reportId - Report ID
 * @param {Object} rewriteData - Rewrite request data
 * @param {string[]} rewriteData.originalPowerNumbers - Original power numbers
 * @param {Object[]} rewriteData.replacementPowers - Replacement power info
 * @param {string} rewriteData.rewriteReason - Optional reason for rewrite
 */
export const executeSubmittedRewrite = async (reportId, rewriteData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rewrite/submitted`, {
      method: 'POST',
      body: JSON.stringify(rewriteData),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || result.errors?.join(', ') || 'Rewrite failed', data: result };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Execute submitted rewrite error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get rewrites for a report
 * @param {number} reportId - Report ID
 */
export const getRewritesByReport = async (reportId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/rewrites`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get rewrites', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get rewrites by report error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get rewrite history for a specific power number
 * @param {string} powerNumber - Power number
 */
export const getPowerRewriteHistory = async (powerNumber) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/powers/${powerNumber}/rewrite-history`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get rewrite history' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get power rewrite history error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// RECEIPT OF POWERS
// ============================================

/**
 * Get pending receipt status for the current user
 * Returns count of pending power shipments awaiting confirmation
 * @returns {Object} { success, data: { pendingCount: number, hasPending: boolean } }
 */
export const getReceiptPendingStatus = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/power-receipts/pending`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get pending status', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get receipt pending status error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Confirm receipt of powers with confirmation code
 * @param {string} confirmationCode - 4-character case-sensitive code (or admin override)
 * @param {number} agentId - Optional agent ID for admin confirming on behalf
 * @returns {Object} { success, data: { confirmedCount: number, orderNumber: string } }
 */
export const confirmPowerReceipt = async (confirmationCode, agentId = null) => {
  try {
    const body = { confirmationCode };
    if (agentId) {
      body.agentId = agentId;
    }

    const response = await authFetch(`${BASE_URL}oath/power-receipts/confirm`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Invalid confirmation code' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Confirm power receipt error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get all pending receipt orders (Admin view)
 * @param {Object} params - Optional filter/sort parameters
 * @param {string} params.searchTerm - Search by agency name or order ID
 * @param {number} params.pageNumber - Page number (1-indexed)
 * @param {number} params.pageSize - Items per page
 * @param {number} params.agentId - Filter by specific agency ID
 * @returns {Object} { success, data: { items, totalCount, agencies } }
 */
export const getReceiptOrders = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.agentId) queryParams.append('agentId', params.agentId.toString());

    const queryString = queryParams.toString();
    const url = `${BASE_URL}oath/power-receipts/orders${queryString ? `?${queryString}` : ''}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get receipt orders', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get receipt orders error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get details for a specific receipt order (Admin view)
 * @param {number} orderId - Order ID
 * @returns {Object} { success, data: ReceiptOrderDetailDto }
 */
export const getReceiptOrderDetails = async (orderId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/power-receipts/orders/${orderId}/details`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get order details', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get receipt order details error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Look up receipt details by confirmation code (preview before confirmation)
 * @param {string} confirmationCode - 4-character confirmation code
 * @returns {Object} { success, data: { found, orderId, agentId, agencyName, orderNumber, shipDate, powerCount, powers } }
 */
export const lookupReceiptByCode = async (confirmationCode) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/power-receipts/lookup`, {
      method: 'POST',
      body: JSON.stringify({ confirmationCode }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to lookup receipt', data: null };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Lookup receipt by code error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// UNEXECUTED TRANSFER BONDS
// ============================================

/**
 * Add transfer powers to a report using their existing transfer bond data.
 * This is the simple path for adding UT powers - the backend will use the
 * existing transfer bond execution details to populate the power report entry.
 * @param {number} reportId - Report ID
 * @param {number[]} powerIds - Array of power IDs to add
 * @returns {Object} { success, data: { addedCount: number, powers: PowerDto[] } }
 */
export const addTransferPowersToReport = async (reportId, powerIds) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/${reportId}/transfer-powers`, {
      method: 'POST',
      body: JSON.stringify({ powerIds }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to add transfer powers' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Add transfer powers to report error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get executing transfer bonds for the agency.
 * These are powers with UT status that the agency received from other agents.
 * @param {number} agencyId - Agency ID
 * @returns {Object} { success, data: UnexecutedTransferPowerDto[] }
 */
export const getExecutingTransferBonds = async (agencyId) => {
  try {
    const response = await authFetch(
      `${BASE_URL}oath/transferbond/${agencyId}/unreported-transfer-bonds`
    );
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch executing transfers', data: [] };
    }
    return { success: true, data: result || [] };
  } catch (error) {
    console.error('Get executing transfer bonds error:', error);
    return { success: false, error: 'Network error fetching executing transfers', data: [] };
  }
};

/**
 * Get requesting transfer bonds (batches) for the agency.
 * These are transfer bond batches from powers the agency sent to other agents that need payment.
 * Note: This is already included in getBatchesForSubmission, but this provides direct access.
 * @param {number} agencyId - Agency ID
 * @returns {Object} { success, data: TransferBondBatchDto[] }
 */
export const getRequestingTransferBatches = async (agencyId) => {
  try {
    // Get batches for submission which includes transfer bond batches
    const response = await authFetch(`${BASE_URL}oath/powerreports/batches-for-submission?agencyId=${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to fetch requesting transfers', data: [] };
    }

    // Filter to only transfer bond batches, and hide credits at the service boundary
    // so any future consumer of this function gets the same credit-free shape as the
    // other batch endpoints. (Sanitizer also drops fully-credit batches.)
    const transferBatches = (result || []).filter(b => b.batchType === 'TransferBond');
    return { success: true, data: sanitizeBatchesHideCredits(transferBatches) };
  } catch (error) {
    console.error('Get requesting transfer batches error:', error);
    return { success: false, error: 'Network error fetching requesting transfers', data: [] };
  }
};

// ============================================
// ACH INFO REQUEST
// ============================================

/**
 * Request ACH payment information for an agency
 * @param {number|string} agencyId
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const requestAchInfo = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerreports/agency/${agencyId}/ach-info-request`, {
      method: 'POST',
    });

    const data = await response.json();

    if (response.ok && data.success) {
      return { success: true, message: data.message };
    }

    return { success: false, error: data.message || 'Failed to send ACH information request' };
  } catch (error) {
    console.error('ACH info request error:', error);
    return { success: false, error: 'Network error sending ACH information request' };
  }
};

// ============================================
// CONSTANTS
// ============================================

export const DEFAULT_PAGE_SIZE = {
  REPORTS: 10,
  POWERS: 25,
};

export const AUTO_REFRESH_INTERVAL_MS = 30000; // 30 seconds
